import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, { Fragment } from 'react';

import Alert from '@/core/components/Alert';
import DonationCard from '@/core/components/DonationCard';
import Header from '@/core/components/Header';
import Icons from '@/core/components/Icons';
import ProfileActions from '@/core/components/ProfileActions';
import ProfileDetails from '@/core/components/ProfileDetails';
import useDisclosure from '@/core/hooks/useDisclosure';
import { BannerSlot, useAdsContext } from '@/core/lib/ads/ads.context';
import { useComponentsContext } from '@/core/lib/components/components.context';
import Store from '@/core/lib/new-architecture/store';
import { useRouterContext } from '@/core/lib/router/router.context';
import Routes from '@/core/lib/router/routes';
import { useTranslationContext } from '@/core/lib/translation/translation.context';
import type { PageWithLayoutsAndProviders } from '@/core/pages/page.types';

dayjs.extend(utc);
dayjs.locale('fr');

const PublicProfile: PageWithLayoutsAndProviders = () => {
  const { t } = useTranslationContext(['common', 'profile.profile-public']);
  const { back, query } = useRouterContext();
  const { Page, Content } = useComponentsContext();
  const { Ad } = useAdsContext();
  const { Head } = useComponentsContext();

  const onBack = () => back(new Routes.ProfileRoute({}));

  const { data: user, isLoading, errors } = Store.user.usePublicProfile(query.uuid);

  const donations = user?.getDonations() ?? [];
  const nbDonations = donations.length;
  const actions = user?.getActions() ?? [];

  const { isOpen: isActionsOpen, onOpen: onOpenActions, onClose: onCloseActions } = useDisclosure();

  const isPrivate = user?.isPrivate();
  const info = user?.getPublicInfo();

  const navbarContent = isPrivate ? t('nav-content', { ns: 'profile.profile-public' }) : <span className="inline-block lg:hidden">{info?.username}</span>;

  return (
    <Page>
      <Head>
        <meta name="robots" content="noindex, nofollow" />
      </Head>
      <Content>
        <div className="relative">
          <Header
            content={navbarContent}
            iconBefore="arrow-left"
            onBefore={onBack}
            iconAfter={actions.length > 0 ? 'more-vertical' : undefined}
            onAfter={actions.length > 0 ? onOpenActions : undefined}
          />
          <ProfileActions actions={actions} isOpen={isActionsOpen} onClose={onCloseActions} />
        </div>

        {isLoading ||
          (!user && (
            <div className="flex size-full items-center justify-center">
              <Icons icon="spinner" size="40" color="button-default" />
            </div>
          ))}

        {!!errors && <Alert status="error">{t('errors.unknown', { ns: 'common' })}</Alert>}

        {!isLoading && user && <ProfileDetails user={user} />}

        {!isLoading && donations.length > 0 && (
          <section className="my-4 w-full lg:my-7" aria-label={t('donations', { ns: 'profile.profile-public' })}>
            <p className="font-bold text-content-primary">
              {t('components.profile.public.donations.donationsList', { ns: 'common', nbDonations: nbDonations.toString(), plural: nbDonations > 1 ? 's' : '' })}
            </p>
            <div className="mt-4 grid w-full grid-cols-2 gap-4 lg:mt-7 lg:grid-cols-4 lg:gap-3">
              {donations.map((donation, index) => {
                return (
                  <Fragment key={`${donation.data.donation.id}-Fragment`}>
                    <div key={donation.data.donation.id} className="col-span-1">
                      <DonationCard donation={donation} vertical />
                    </div>
                    {index > 0 && (index + 1) % 8 === 0 && (
                      <section key={`${donation.data.donation.id}-ad`} className="col-span-2 flex items-center justify-center lg:col-span-4">
                        <div className="hidden place-self-center md:col-span-8 min-[1296px]:flex">
                          <Ad size="728x90" slot={BannerSlot.DONNONS_PROFIL_MIDDLE} />
                        </div>
                        <div className="hidden place-self-center lg:col-span-8 lg:max-[1295px]:flex">
                          <Ad size="336x280" slot={BannerSlot.DONNONS_PROFIL_MIDDLE} />
                        </div>
                        <div className="col-span-4 hidden place-self-center min-[728px]:max-[1023px]:flex md:col-span-8">
                          <Ad size="728x90" slot={BannerSlot.DONNONS_PROFIL_MIDDLE} />
                        </div>
                        <div className="col-span-4 hidden place-self-center min-[336px]:max-[727px]:flex">
                          <Ad size="336x280" slot={BannerSlot.DONNONS_PROFIL_MIDDLE} />
                        </div>
                        <div className="col-span-4 hidden place-self-center min-[320px]:max-[335px]:flex">
                          <Ad size="320x100" slot={BannerSlot.DONNONS_PROFIL_MIDDLE} />
                        </div>
                        <div className="col-span-4 hidden place-self-center min-[300px]:max-[319px]:flex">
                          <Ad size="300x250" slot={BannerSlot.DONNONS_PROFIL_MIDDLE} />
                        </div>
                      </section>
                    )}
                  </Fragment>
                );
              })}
            </div>
          </section>
        )}
        {!isLoading && donations.length === 0 && (
          <div className="flex w-full justify-center px-4">
            <div className="flex h-full w-100 flex-col items-center justify-items-start">
              <img src="/assets/empty/profile-donations-current.svg" className="size-45" alt={t('components.profile.public.empty-donations', { ns: 'common' })} />
              <h1 className="text-body-secondary mb-9 text-center font-semibold text-content-primary">{t('components.profile.public.empty-donations', { ns: 'common' })}</h1>
            </div>
          </div>
        )}
      </Content>
    </Page>
  );
};

export default PublicProfile;
